import { useState } from "react";
import { inventoryApi } from "../api";
import { InventoryImportBatchSummary } from "../types";
function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}
export function InventoryUploadPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<InventoryImportBatchSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function run(action: () => Promise<InventoryImportBatchSummary | Blob>, filename?: string) {
    try {
      setBusy(true);
      setError(null);
      const response = await action();
      if (response instanceof Blob && filename) {
        downloadBlob(response, filename);
      } else {
        setResult(response as InventoryImportBatchSummary);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <h2 className="text-lg font-bold text-slate-900">Item Master Upload</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => run(() => inventoryApi.downloadItemMasterTemplate(), "inventory-item-master-template.xlsx")}
            title="Item Code is grey (reference only); every other column is green and editable. Type a Category Name directly, or see the categories-reference sheet for the exact Category Code."
            className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Download Template
          </button>
          <label className="cursor-pointer rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
            Upload Excel
            <input hidden type="file" accept=".xlsx" onChange={(e) => e.target.files?.[0] && run(() => inventoryApi.uploadItemMaster(e.target.files![0]))} />
          </label>
        </div>
      </div>
      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <h2 className="text-lg font-bold text-slate-900">Stock Correction Upload</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => run(() => inventoryApi.downloadStockTemplate(), "inventory-stock-template.xlsx")}
            title="Comes pre-filled with every existing item — just edit the highlighted stock column and re-upload"
            className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Download Template
          </button>
          <label className="cursor-pointer rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
            Upload Excel
            <input hidden type="file" accept=".xlsx" onChange={(e) => e.target.files?.[0] && run(() => inventoryApi.uploadStockCorrection(e.target.files![0]))} />
          </label>
        </div>
      </div>
      {busy && <div className="lg:col-span-2 rounded-2xl border bg-white p-4 text-sm text-slate-500">Processing...</div>}
      {error && <div className="lg:col-span-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {result && (
        <div className="lg:col-span-2 rounded-2xl border bg-white p-5 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900">Latest Upload Result</h3>
          <div className="mt-3 grid gap-3 md:grid-cols-4">
            <Stat label="Batch" value={result.batchNo} />
            <Stat label="Status" value={result.status} />
            <Stat label="Valid Rows" value={result.validRows} />
            <Stat label="Invalid Rows" value={result.invalidRows} />
          </div>
          {/* Only show the action(s) actually valid for the batch's current status — showing all
             four unconditionally let people click e.g. Post/Reject on a PENDING_APPROVAL batch
             and always get a 409. Approve also auto-posts server-side in the same call (confirmed
             2026-07-09), so there is deliberately no separate "Post" button here either. */}
          {(result.status === "VALIDATED" || result.status === "PARTIALLY_VALID") && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button disabled={busy} onClick={() => run(() => inventoryApi.submitImport(result.id))} className="rounded-xl border border-amber-200 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50">Submit</button>
            </div>
          )}
          {result.status === "PENDING_APPROVAL" && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button disabled={busy} onClick={() => run(() => inventoryApi.approveImport(result.id))} className="rounded-xl border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">Approve</button>
              <button disabled={busy} onClick={() => run(() => inventoryApi.rejectImport(result.id))} className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">Reject</button>
            </div>
          )}
          {(result.status === "APPROVED" || result.status === "POSTED" || result.status === "REJECTED") && (
            <p className="mt-4 text-sm text-slate-500">
              {result.status === "POSTED" ? "This batch has been posted." : result.status === "REJECTED" ? "This batch was rejected." : "Awaiting posting."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border bg-slate-50 p-3">
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-1 font-bold text-slate-900">{value}</div>
    </div>
  );
}
